import { pageModule } from '@components/pageModule';
import { call } from '@packages/functions';
import * as React from 'react';
import { ComponentLoader, Loading } from './Loading';

type WidgetProps = Record<string, unknown>;
type WidgetComponent = React.ComponentType<WidgetProps>;

type Module = WidgetComponent | Promise<unknown> | unknown;
type ModLoader = (() => Module) | Module;

type callFunc = (...args: unknown[]) => unknown;

// Cache lazy components by mod reference — React.lazy() must only be called once per module
const lazyCache = new WeakMap<Function, React.LazyExoticComponent<WidgetComponent>>();

function createLazyComponent(mod: ModLoader, onLoad?: callFunc): React.LazyExoticComponent<WidgetComponent> {
    const cacheKey = typeof mod === 'function' ? (mod as Function) : null;

    if (cacheKey && lazyCache.has(cacheKey)) {
        return lazyCache.get(cacheKey)!;
    }

    const LazyComp = React.lazy(async (...args: unknown[]) => {
        // Supports functions, promises, and direct React elements as mod
        const ret = await (typeof mod === 'function' ? (mod as (...a: unknown[]) => unknown)(...args) : mod);

        call(onLoad, ...args);

        if (React.isValidElement(ret)) {
            return { default: (() => ret as React.ReactElement) as WidgetComponent };
        }

        if (ret && typeof ret === 'object' && 'default' in ret) {
            return ret as { default: WidgetComponent };
        }

        return { default: ret as WidgetComponent };
    });

    if (cacheKey) {
        lazyCache.set(cacheKey, LazyComp);
    }

    return LazyComp;
}

interface LoadingComponentProps {
    error?: Error | null;
    retry?: () => void;
    isLoading?: boolean;
    pastDelay?: boolean;
    timedOut?: boolean;
}

interface ErrorBoundaryProps {
    LoadingComponent: React.ComponentType<LoadingComponentProps>;
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    error: Error | null;
}

// Catches chunk load failures and surfaces error + retry to the LoadingComponent
class AsyncErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static displayName = 'AsyncErrorBoundary';
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    retry = () => {
        this.setState({ error: null });
    };

    render() {
        const { error } = this.state;
        const { LoadingComponent, children } = this.props;

        if (error) {
            return (
                <LoadingComponent
                    error={error}
                    retry={this.retry}
                    isLoading={false}
                    pastDelay={false}
                    timedOut={false}
                />
            );
        }

        return children;
    }
}

interface AsyncComponentProps {
    mod: ModLoader;
    loadingComponent?: React.ComponentType<LoadingComponentProps>;
    onLoad?: callFunc;
    [key: string]: unknown;
}

interface AsyncComponentState {
    Widget?: React.LazyExoticComponent<WidgetComponent>;
    props?: Record<string, unknown>;
}

class AsyncComponent extends React.Component<AsyncComponentProps, AsyncComponentState> {
    state: AsyncComponentState = {};

    static getDerivedStateFromProps({
        mod,
        loadingComponent: _loadingComponent,
        onLoad,
        ...props
    }: AsyncComponentProps): AsyncComponentState {
        const Widget = createLazyComponent(mod, onLoad);
        return { Widget, props };
    }

    shouldComponentUpdate(nextProps: AsyncComponentProps) {
        return this.props.mod !== nextProps.mod;
    }

    render() {
        const { Widget, props } = this.state;

        if (!Widget) {
            return null;
        }

        const LoadingComponent = this.props.loadingComponent || Loading;

        return (
            <AsyncErrorBoundary LoadingComponent={LoadingComponent}>
                <React.Suspense fallback={<LoadingComponent isLoading pastDelay />}>
                    <Widget {...props} />
                </React.Suspense>
            </AsyncErrorBoundary>
        );
    }
}

export const AsyncPageRender = (mod: ModLoader, props: Record<string, unknown> = {}): React.ReactElement => (
    <AsyncComponent mod={mod} {...props} />
);

export const AsyncComponentRender = (mod: () => Module, props?: Record<string, unknown>): React.ReactElement =>
    AsyncPageRender(() => pageModule(mod()), {
        ...props,
        loadingComponent: ComponentLoader,
    });
